import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@devhop/ui";
import { CircleHelpIcon, PencilIcon, PlusIcon } from "lucide-react";
import { useState } from "react";
import { FormLotteryPrizeDialog } from "@/modules/forms/presentation/ui/builder/FormLotteryPrizeDialog";
import {
  useCreateFormLotteryPrizeMutation,
  useDrawFormLotteryPrizeMutation,
  useFormLotteryEntriesQuery,
  useFormLotteryQuery,
  usePatchFormLotteryPrizeMutation,
  useRedeemFormLotteryEntryMutation,
  useSaveFormLotterySettingsMutation,
} from "@/modules/forms/presentation/api/queries";
import type {
  FormFieldDTO,
  FormLotteryPrizeDTO,
  FormLotteryPrizeInputDTO,
} from "@/modules/forms/presentation/api/types";
import { QueryState } from "@/shared/ui/QueryState";

type FormLotterySectionProps = {
  formId: string;
  fields: FormFieldDTO[];
  canUpdate: boolean;
};

const FIELD_NONE = "__field_none__";

const DRAW_MODE_LABELS: Record<"instant" | "deferred", string> = {
  instant: "ທັນທີ (ຕອນສົ່ງຟອມ)",
  deferred: "ຫຼັງ (ສຸ່ມໃນງານ)",
};

const DRAW_MODE_DESCRIPTIONS: Record<"instant" | "deferred", string> = {
  instant:
    "ສຸ່ມຜົນທັນທີເມື່ອຜູ້ໃຊ້ສົ່ງຟອມ — ໄດ້ລາງວັນ/ໂຄດຮັບທັນທີ (ຖ້າສະຕັອກຍັງພໍ).",
  deferred:
    "ບໍ່ສຸ່ມຕອນສົ່ງຟອມ — ຜູ້ດູແລກົດ «ສຸ່ມລາງວັນ» ທີ່ຫຼັງ ຈາກຜູ້ລົງທະບຽນທີ່ຍັງບໍ່ມີໂຄດຮັບລາງວັນ.",
};

export function FormLotterySection({
  formId,
  fields,
  canUpdate,
}: FormLotterySectionProps) {
  const lotteryQuery = useFormLotteryQuery(formId);
  const [search, setSearch] = useState("");
  const entriesQuery = useFormLotteryEntriesQuery(formId, {
    limit: 20,
    offset: 0,
    search: search.trim() || undefined,
  });
  const saveSettings = useSaveFormLotterySettingsMutation(formId);
  const createPrize = useCreateFormLotteryPrizeMutation(formId);
  const patchPrize = usePatchFormLotteryPrizeMutation(formId);
  const drawPrize = useDrawFormLotteryPrizeMutation(formId);
  const redeem = useRedeemFormLotteryEntryMutation(formId);
  const [prizeDialogOpen, setPrizeDialogOpen] = useState(false);
  const [editingPrize, setEditingPrize] = useState<FormLotteryPrizeDTO | null>(
    null,
  );

  const closePrizeDialog = () => {
    setPrizeDialogOpen(false);
    setEditingPrize(null);
  };

  const textFields = fields.filter((f) => f.type === "text" || f.type === "number");

  return (
    <QueryState result={lotteryQuery} title="ກຳລັງໂຫຼດການຕັ້ງຄ່າລຸ້ນລາງວັນ">
      {!lotteryQuery.data ? null : (
        <LotteryBody
          canUpdate={canUpdate}
          textFields={textFields}
          settings={lotteryQuery.data.settings}
          prizes={lotteryQuery.data.prizes}
          isSaving={saveSettings.isPending}
          isPrizeSaving={createPrize.isPending || patchPrize.isPending}
          prizeDialogOpen={prizeDialogOpen}
          editingPrize={editingPrize}
          onPrizeDialogOpenChange={(open) => {
            setPrizeDialogOpen(open);
            if (!open) setEditingPrize(null);
          }}
          onOpenCreatePrize={() => {
            setEditingPrize(null);
            setPrizeDialogOpen(true);
          }}
          onOpenEditPrize={(prize) => {
            setEditingPrize(prize);
            setPrizeDialogOpen(true);
          }}
          onSaveSettings={(body) => saveSettings.mutate(body)}
          onCreatePrize={(body) =>
            createPrize.mutate(body, { onSuccess: closePrizeDialog })
          }
          onUpdatePrize={(prizeId, body) =>
            patchPrize.mutate(
              { prizeId, body },
              { onSuccess: closePrizeDialog },
            )
          }
          prizeCount={lotteryQuery.data.prizes.length}
          onDraw={(prizeId) => drawPrize.mutate({ prizeId })}
          entries={entriesQuery.data?.data ?? []}
          entriesTotal={entriesQuery.data?.meta.total ?? 0}
          search={search}
          onSearchChange={setSearch}
          onRedeem={(entryId) => redeem.mutate(entryId)}
          isDrawing={drawPrize.isPending}
        />
      )}
    </QueryState>
  );
}

function LotteryBody({
  canUpdate,
  textFields,
  settings,
  prizes,
  isSaving,
  isPrizeSaving,
  prizeDialogOpen,
  editingPrize,
  onPrizeDialogOpenChange,
  onOpenCreatePrize,
  onOpenEditPrize,
  onSaveSettings,
  onCreatePrize,
  onUpdatePrize,
  prizeCount,
  onDraw,
  entries,
  entriesTotal,
  search,
  onSearchChange,
  onRedeem,
  isDrawing,
}: {
  canUpdate: boolean;
  textFields: FormFieldDTO[];
  settings: {
    enabled: boolean;
    nameFieldId: string;
    nicknameFieldId: string | null;
    uniqueFieldId: string;
    successMessage: string | null;
    missMessage: string | null;
  };
  prizes: FormLotteryPrizeDTO[];
  isSaving: boolean;
  isPrizeSaving: boolean;
  prizeDialogOpen: boolean;
  editingPrize: FormLotteryPrizeDTO | null;
  onPrizeDialogOpenChange: (open: boolean) => void;
  onOpenCreatePrize: () => void;
  onOpenEditPrize: (prize: FormLotteryPrizeDTO) => void;
  onSaveSettings: (body: {
    enabled: boolean;
    nameFieldId: string;
    nicknameFieldId: string | null;
    uniqueFieldId: string;
    successMessage: string | null;
    missMessage: string | null;
  }) => void;
  onCreatePrize: (body: FormLotteryPrizeInputDTO) => void;
  onUpdatePrize: (
    prizeId: string,
    body: Partial<FormLotteryPrizeInputDTO>,
  ) => void;
  prizeCount: number;
  onDraw: (prizeId: string) => void;
  entries: Array<{
    id: string;
    fullName: string;
    uniqueKey: string;
    prizeName: string | null;
    redeemCode: string | null;
    redeemedAt: string | null;
  }>;
  entriesTotal: number;
  search: string;
  onSearchChange: (value: string) => void;
  onRedeem: (entryId: string) => void;
  isDrawing: boolean;
}) {
  const [enabled, setEnabled] = useState(settings.enabled);
  const [nameFieldId, setNameFieldId] = useState(settings.nameFieldId);
  const [nicknameFieldId, setNicknameFieldId] = useState(
    settings.nicknameFieldId ?? "",
  );
  const [uniqueFieldId, setUniqueFieldId] = useState(settings.uniqueFieldId);
  const [successMessage, setSuccessMessage] = useState(settings.successMessage ?? "");
  const [missMessage, setMissMessage] = useState(settings.missMessage ?? "");

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ລຸ້ນລາງວັນຕອນລົງທະບຽນ</CardTitle>
          <CardDescription>
            ເມື່ອເປີດໃຊ້ງານ ຟອມ public ຈະສຸ່ມລາງວັນທັນທີຕາມນ້ຳໜັກ (ແບບດຽວກັນກັບລົງທະບຽນຜູ້ເຂົ້າຊົມ Summer Camp).
            ເບີໂທຊ້ຳຈະໄດ້ຜົນເດີມ. ລາງວັນຈິງມີໂຄດຮັບຂອງ; ລາງວັນປົລອບໃຈບໍ່ຕັດສະຕັອກ.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={enabled}
              disabled={!canUpdate}
              onCheckedChange={(v) => setEnabled(v === true)}
            />
            ເປີດລຸ້ນລາງວັນສຳລັບຟອມນີ້
          </label>
          <div className="grid gap-4 md:grid-cols-3">
            <FieldSelect
              id="lottery-name"
              label="ຊ່ອງຊື່ເຕັມ"
              value={nameFieldId}
              fields={textFields}
              allowEmpty={false}
              disabled={!canUpdate}
              onChange={setNameFieldId}
            />
            <FieldSelect
              id="lottery-nick"
              label="ຊ່ອງຊື່ຫຼິ້ນ"
              value={nicknameFieldId}
              fields={textFields}
              allowEmpty
              disabled={!canUpdate}
              onChange={setNicknameFieldId}
            />
            <FieldSelect
              id="lottery-unique"
              label="ຊ່ອງເບີໂທ (ຫ້າມຊ້ຳ)"
              value={uniqueFieldId}
              fields={textFields}
              allowEmpty={false}
              disabled={!canUpdate}
              onChange={setUniqueFieldId}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="lottery-win">ຂໍ້ຄວາມເມື່ອຖືກລາງວັນ</Label>
              <Textarea
                id="lottery-win"
                rows={2}
                disabled={!canUpdate}
                value={successMessage}
                onChange={(e) => setSuccessMessage(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="lottery-miss">ຂໍ້ຄວາມເມື່ອບໍ່ຖືກລາງວັນ / ຂອບໃຈ</Label>
              <Textarea
                id="lottery-miss"
                rows={2}
                disabled={!canUpdate}
                value={missMessage}
                onChange={(e) => setMissMessage(e.target.value)}
              />
            </div>
          </div>
          {canUpdate ? (
            <Button
              type="button"
              disabled={isSaving || !nameFieldId || !uniqueFieldId}
              onClick={() =>
                onSaveSettings({
                  enabled,
                  nameFieldId,
                  nicknameFieldId: nicknameFieldId || null,
                  uniqueFieldId,
                  successMessage: successMessage.trim() || null,
                  missMessage: missMessage.trim() || null,
                })
              }
            >
              ບັນທຶກການຕັ້ງຄ່າ
            </Button>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
          <div className="space-y-1">
            <CardTitle className="text-lg">ລາງວັນ</CardTitle>
            <CardDescription>
              ໂໝດທັນທີ = ສຸ່ມໃນຂະນະສົ່ງຟອມ · ໂໝດຫຼັງ = ສຸ່ມຈາກຜູ້ລົງທະບຽນທີ່ຍັງບໍ່ມີໂຄດຮັບລາງວັນ
            </CardDescription>
          </div>
          {canUpdate ? (
            <Button
              type="button"
              size="sm"
              className="gap-1.5"
              onClick={onOpenCreatePrize}
            >
              <PlusIcon className="size-4" aria-hidden="true" />
              ເພີ່ມລາງວັນ
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">ຊື່</th>
                  <th className="py-2 pr-3 font-medium">
                    <span className="inline-flex items-center gap-1">
                      ໂໝດ
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              aria-label="ຄຳອະທິບາຍໂໝດການສຸ່ມ"
                            >
                              <CircleHelpIcon
                                className="size-3.5 shrink-0"
                                aria-hidden="true"
                              />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-xs space-y-2 border border-border bg-popover text-left text-popover-foreground text-xs font-normal shadow-md"
                          >
                            <div>
                              <p className="font-semibold text-popover-foreground">
                                {DRAW_MODE_LABELS.instant}
                              </p>
                              <p className="text-popover-foreground/90">
                                {DRAW_MODE_DESCRIPTIONS.instant}
                              </p>
                            </div>
                            <div>
                              <p className="font-semibold text-popover-foreground">
                                {DRAW_MODE_LABELS.deferred}
                              </p>
                              <p className="text-popover-foreground/90">
                                {DRAW_MODE_DESCRIPTIONS.deferred}
                              </p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </span>
                  </th>
                  <th className="py-2 pr-3 font-medium">ເຫຼືອ</th>
                  <th className="py-2 pr-3 font-medium">ນ້ຳໜັກ</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {prizes.map((prize) => (
                  <tr key={prize.id} className="border-b">
                    <td className="py-2 pr-3">
                      {prize.name}
                      {prize.isConsolation ? " (ປົລອບໃຈ)" : ""}
                    </td>
                    <td className="py-2 pr-3">
                      {DRAW_MODE_LABELS[prize.drawMode]}
                    </td>
                    <td className="py-2 pr-3">
                      {prize.isConsolation ? "—" : `${prize.remaining}/${prize.quantity}`}
                    </td>
                    <td className="py-2 pr-3">{prize.probabilityWeight}</td>
                    <td className="py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {canUpdate ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            aria-label={`ແກ້ໄຂ ${prize.name}`}
                            onClick={() => onOpenEditPrize(prize)}
                          >
                            <PencilIcon className="size-4" aria-hidden="true" />
                          </Button>
                        ) : null}
                        {canUpdate &&
                        prize.drawMode === "deferred" &&
                        !prize.isConsolation ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={isDrawing || prize.remaining <= 0}
                            onClick={() => onDraw(prize.id)}
                          >
                            ສຸ່ມລາງວັນ
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <FormLotteryPrizeDialog
            open={prizeDialogOpen}
            onOpenChange={onPrizeDialogOpenChange}
            prize={editingPrize}
            sortOrder={prizeCount + 1}
            isPending={isPrizeSaving}
            onCreate={onCreatePrize}
            onUpdate={onUpdatePrize}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ຜູ້ລົງທະບຽນ ({entriesTotal})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            value={search}
            placeholder="ຄົ້ນຊື່, ເບີໂທ ຫຼື ໂຄດຮັບລາງວັນ"
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">ຊື່</th>
                  <th className="py-2 pr-3 font-medium">ເບີ</th>
                  <th className="py-2 pr-3 font-medium">ລາງວັນ</th>
                  <th className="py-2 pr-3 font-medium">ໂຄດຮັບລາງວັນ</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} className="border-b">
                    <td className="py-2 pr-3">{entry.fullName}</td>
                    <td className="py-2 pr-3">{entry.uniqueKey}</td>
                    <td className="py-2 pr-3">{entry.prizeName ?? "—"}</td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {entry.redeemCode ?? "—"}
                    </td>
                    <td className="py-2 text-right">
                      {canUpdate && entry.redeemCode && !entry.redeemedAt ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => onRedeem(entry.id)}
                        >
                          ຢືນຢັນຮັບແລ້ວ
                        </Button>
                      ) : entry.redeemedAt ? (
                        <span className="text-muted-foreground text-xs">ຮັບລາງວັນແລ້ວ</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FieldSelect({
  id,
  label,
  value,
  fields,
  allowEmpty,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  fields: FormFieldDTO[];
  allowEmpty: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const selectValue = value.trim().length > 0 ? value : FIELD_NONE;
  const placeholder = allowEmpty ? "— ບໍ່ໃຊ້ —" : "— ເລືອກຊ່ອງ —";

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={selectValue}
        disabled={disabled}
        onValueChange={(v) => onChange(v === FIELD_NONE ? "" : v)}
      >
        <SelectTrigger id={id}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={FIELD_NONE}>{placeholder}</SelectItem>
          {fields.map((field) => (
            <SelectItem key={field.id} value={field.id}>
              {field.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
